import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from './CartContext';
import { useSellerDomain } from './SellerDomainContext';
import { useTheme } from './ThemeContext';

export default function Navbar() {
  const { cartCount } = useCart();
  const location = useLocation();
  const sellerDomain = useSellerDomain();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [infoPanel, setInfoPanel] = useState(null); // 'about' | 'tnc' | null
  const navRef = useRef(null);

  useEffect(() => { setMenuOpen(false); setInfoPanel(null); }, [location.pathname]);

  // Close panel on click outside the entire navbar+panel area
  useEffect(() => {
    if (!infoPanel) return;
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setInfoPanel(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [infoPanel]);

  function isActive(path) { return location.pathname === path; }

  const close = () => { setMenuOpen(false); setInfoPanel(null); };
  const togglePanel = (tab) => {
    setMenuOpen(false);
    setInfoPanel(prev => prev === tab ? null : tab);
  };

  const hasAbout = !!sellerDomain;
  const hasTnC   = !!sellerDomain;

  return (
    <div ref={navRef}>
      <nav className="navbar">
        <div className="navbar-inner">
          {/* Brand — logo + name */}
          <Link to="/" className="navbar-brand" onClick={close}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {sellerDomain ? (
              <>
                {sellerDomain.logo_url ? (
                  <img
                    src={sellerDomain.logo_url}
                    alt={sellerDomain.business_name}
                    style={{ height: 32, maxWidth: 80, objectFit: 'contain', borderRadius: 4 }}
                  />
                ) : (
                  <span style={{ fontSize: 18 }}>🏪</span>
                )}
                <span>{sellerDomain.business_name}</span>
              </>
            ) : (
              '🛍️ SmaBizZ Market'
            )}
          </Link>

          <div className={`navbar-links${menuOpen ? ' open' : ''}`}>
            <Link to="/track" className="nav-link"
              style={isActive('/track') ? { color: 'var(--primary)' } : {}} onClick={close}>
              <span style={{ fontSize: 16 }}>📦</span>
              <span>Track Order</span>
            </Link>
            <Link to="/seller" className="nav-link"
              style={isActive('/seller') ? { color: 'var(--primary)' } : {}} onClick={close}>
              <span style={{ fontSize: 16 }}>🏪</span>
              <span>Seller Portal</span>
            </Link>
            <Link to="/cart" className="nav-link nav-cart" onClick={close}>
              <span style={{ fontSize: 20 }}>🛒</span>
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>
              )}
              <span>Cart{cartCount > 0 ? ` (${cartCount})` : ''}</span>
            </Link>

            {/* Dark / Light toggle */}
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                background: 'none', border: '1.5px solid var(--border)',
                borderRadius: 20, cursor: 'pointer', padding: '4px 10px',
                fontSize: 16, display: 'flex', alignItems: 'center', gap: 4,
                color: 'var(--text)', transition: 'border-color 0.15s',
              }}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {/* About Us — end of nav, only on seller domain */}
            {hasAbout && (
              <button className="nav-link" onClick={() => togglePanel('about')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: infoPanel === 'about' ? 'var(--primary)' : undefined,
                  fontWeight: infoPanel === 'about' ? 700 : undefined,
                }}>
                <span style={{ fontSize: 16 }}>ℹ️</span>
                <span>About Us</span>
              </button>
            )}

            {/* Terms & Conditions — end of nav, only on seller domain */}
            {hasTnC && (
              <button className="nav-link" onClick={() => togglePanel('tnc')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: infoPanel === 'tnc' ? 'var(--primary)' : undefined,
                  fontWeight: infoPanel === 'tnc' ? 700 : undefined,
                }}>
                <span style={{ fontSize: 16 }}>📋</span>
                <span>Terms &amp; Conditions</span>
              </button>
            )}
          </div>

          <button className="nav-hamburger" onClick={() => setMenuOpen(m => !m)}
            aria-label="Toggle menu">
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Slide-down info panel — closes on outside click or mouse leaving the panel */}
      {infoPanel && (
        <div
          onMouseLeave={() => setInfoPanel(null)}
          style={{
            background: 'var(--surface)',
            borderBottom: '2px solid var(--border)',
            padding: '16px 24px',
          }}
        >
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: 'var(--text)' }}>
              {infoPanel === 'about' ? 'ℹ️ About Us' : '📋 Terms & Conditions'}
            </div>
            <p style={{
              margin: 0, fontSize: 13, lineHeight: 1.8,
              color: 'var(--muted)', textAlign: 'justify', whiteSpace: 'pre-wrap',
            }}>
              {infoPanel === 'about'
                ? (sellerDomain.description || 'No information added yet.')
                : (sellerDomain.terms_and_conditions || 'No terms and conditions added yet.')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
